import React, {
  createContext,
  useContext,
  useCallback,
  useRef,
  useEffect,
  useState,
} from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useDataApi } from './DataApiContext'
import { useNotificationsProvider } from './NotificationsContext'
import { useTranslation } from 'react-i18next'

const ExportContext = createContext()

const POLL_INTERVAL = 2000 // 2 seconds
const STORAGE_KEY = 'catp_active_exports'
const EXPORT_TIMEOUT = 20 * 60 * 1000 // 20 minutes

// Helper to load from localStorage
function loadActiveExports() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      return new Map(Object.entries(parsed))
    }
  } catch (e) {
    console.error('Error loading active exports:', e)
  }
  return new Map()
}

// Helper to save to localStorage
function saveActiveExports(exports) {
  try {
    const obj = Object.fromEntries(exports)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(obj))
  } catch (e) {
    console.error('Error saving active exports:', e)
  }
}

export function ExportProvider({ children }) {
  const { dataProvider } = useDataApi()
  const { showNotification } = useNotificationsProvider()
  const { t } = useTranslation()

  // Track all active exports - initialize from localStorage
  const activeExportsRef = useRef(loadActiveExports())
  const pollIntervalRef = useRef(null)
  const [activeExportsCount, setActiveExportsCount] = useState(
    activeExportsRef.current.size
  )

  // Update count and persist
  const updateExports = useCallback(() => {
    setActiveExportsCount(activeExportsRef.current.size)
    saveActiveExports(activeExportsRef.current)
  }, [])

  // Start polling when there are active exports
  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) return // Already polling

    pollIntervalRef.current = setInterval(async () => {
      const exports = activeExportsRef.current

      if (exports.size === 0) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
        return
      }

      // Check status of all active exports
      for (const [batchId, exportInfo] of exports.entries()) {
        try {
          // Check for timeout
          const startTime = new Date(exportInfo.startedAt).getTime()
          if (Date.now() - startTime > EXPORT_TIMEOUT) {
            exports.delete(batchId)
            updateExports()
            const notifId = exportInfo.notificationId || batchId
            showNotification('error', t('export.timeout'), { id: notifId })
            continue
          }

          const response = await dataProvider.getAsyncExportStatus(batchId)
          const { status, fileUrl, fileName, errorMessage } = response.data

          if (status === 'completed') {
            exports.delete(batchId)
            updateExports()

            // Update notification with export type info (same ID replaces the processing one)
            const exportLabel = exportInfo.type || 'Export'
            const notifId = exportInfo.notificationId || batchId
            showNotification(
              'success',
              `${t('export.completed')} - ${fileName || exportLabel}`,
              {
                id: notifId,
                persist: true,
                action: {
                  label: t('export.open'),
                  onClick: () => {
                    window.open(fileUrl, '_blank')
                  },
                },
              }
            )
          } else if (status === 'failed') {
            exports.delete(batchId)
            updateExports()
            const notifId = exportInfo.notificationId || batchId
            showNotification(
              'error',
              errorMessage || t('errors.export-failed'),
              {
                id: notifId,
              }
            )
          }
        } catch (error) {
          console.error('Error polling export status:', error)
          exports.delete(batchId)
          updateExports()
          showNotification('error', t('errors.export-failed'))
        }
      }

      // Stop polling if no more active exports
      if (exports.size === 0) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
    }, POLL_INTERVAL)
  }, [dataProvider, showNotification, t, updateExports])

  // Resume polling on mount if there are active exports
  useEffect(() => {
    if (activeExportsRef.current.size > 0) {
      startPolling()
    }
  }, [startPolling])

  const startExport = useCallback(
    async (type, tableState) => {
      // Generate temp ID for notification tracking before we have batchId
      const tempNotificationId = uuidv4()

      try {
        // Show starting notification with temp ID
        showNotification('info', t('export.starting'), {
          id: tempNotificationId,
        })

        // Start the export
        const response = await dataProvider.startAsyncExport(type, tableState)
        const { batchId } = response.data

        // Add to active exports with notification ID mapping
        activeExportsRef.current.set(batchId, {
          type,
          startedAt: new Date().toISOString(),
          notificationId: tempNotificationId,
        })
        updateExports()

        // Update notification to processing (replaces starting)
        showNotification('info', t('export.processing'), {
          id: tempNotificationId,
        })

        // Start polling
        startPolling()

        return { batchId }
      } catch (error) {
        console.error('Export error:', error)
        showNotification('error', error.message || t('errors.export-failed'), {
          id: tempNotificationId,
        })
        throw error
      }
    },
    [dataProvider, showNotification, startPolling, t, updateExports]
  )

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
      }
    }
  }, [])

  const value = {
    startExport,
    activeExportsCount,
  }

  return (
    <ExportContext.Provider value={value}>{children}</ExportContext.Provider>
  )
}

export function useExport() {
  const context = useContext(ExportContext)
  if (context === undefined) {
    throw new Error('useExport must be used within an ExportProvider')
  }
  return context
}

export default ExportContext
